import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["refresh", "fallback", "error"];

  connect() {
    this.fallbackTargets.forEach((button) => {
      button.hidden = true;
    });
    this.openedValues = new Map();
    this.element.querySelectorAll("details[open]").forEach((row) => this.remember(row));
  }

  disconnect() {
    this.request?.abort();
  }

  selectDays(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const checked = button.value.startsWith("select_all_days:");
    button
      .closest("[data-schedule-block]")
      .querySelectorAll('input[type="checkbox"][name$="[weekdays][]"]')
      .forEach((input) => {
        input.checked = checked;
      });
  }

  refresh(event) {
    const field = event.target;
    const top = field.getBoundingClientRect().top;
    this.preview(this.refreshTarget, {
      restore: (replacement) => {
        const nextField = Array.from(replacement.elements).find((control) => control.id === field.id);
        if (!nextField) return;

        nextField.focus({ preventScroll: true });
        this.scroller(replacement).scrollBy(0, nextField.getBoundingClientRect().top - top);
      },
      fail: () => {
        this.fallbackTargets.forEach((button) => {
          button.hidden = false;
        });
        this.errorTarget.hidden = false;
        this.errorTarget.focus();
      },
    });
  }

  // A native in-form action posts the whole page and leaves the address on the create or
  // update route, so a refresh lands somewhere other than the form being edited.
  adjust(event) {
    const button = event.submitter;
    if (button?.name !== "form_action" || this.fallbackTargets.includes(button)) return;
    if (this.nativeSubmitter === button) {
      this.nativeSubmitter = null;
      return;
    }

    event.preventDefault();
    const anchor = button.getAttribute("formaction")?.split("#")[1];
    const top = anchor ? document.getElementById(anchor)?.getBoundingClientRect().top : undefined;
    const removing = button.value.startsWith("remove_");
    const focusValue = removing
      ? button.value
          .replace(/^remove_/, "add_")
          .split(":")
          .slice(0, -1)
          .join(":")
      : button.value;
    this.preview(button, {
      reopen: !removing,
      restore: (replacement) => {
        const target = anchor && document.getElementById(anchor);
        if (target && top === undefined) {
          target.scrollIntoView({ block: "start" });
        } else if (target) {
          this.scroller(replacement).scrollBy(0, target.getBoundingClientRect().top - top);
        }
        Array.from(replacement.elements)
          .find((control) => control.name === button.name && control.value === focusValue)
          ?.focus({ preventScroll: true });
      },
      fail: () => {
        this.nativeSubmitter = button;
        this.element.requestSubmit(button);
      },
    });
  }

  summarize(event) {
    const row = event.target;
    if (row.open) {
      this.remember(row);
      return;
    }
    if (this.request || this.element.querySelector('[aria-invalid="true"]')) return;
    if (this.openedValues.get(row.id) === this.rowValues(row)) return;

    const anchor = this.element.querySelector("details[open]") || row;
    const top = anchor.getBoundingClientRect().top;
    this.preview(this.refreshTarget, {
      keepNotice: true,
      restore: (replacement) => {
        const next = document.getElementById(anchor.id);
        if (next) this.scroller(replacement).scrollBy(0, next.getBoundingClientRect().top - top);
      },
      fail: () => {},
    });
  }

  remember(row) {
    this.openedValues.set(row.id, this.rowValues(row));
  }

  rowValues(row) {
    return JSON.stringify(
      Array.from(row.querySelectorAll("input, select, textarea"), (control) => [
        control.name,
        ["checkbox", "radio"].includes(control.type) ? control.checked : control.value,
      ])
    );
  }

  async preview(submitter, { restore, fail, reopen = true, keepNotice = false }) {
    if (this.request) return;

    const data = new FormData(this.element, submitter);
    data.set(submitter.name, submitter.value);
    const controls = Array.from(this.element.elements).filter((control) => !control.disabled);
    const focus = this.focusedPlace();
    this.request = new AbortController();
    this.element.setAttribute("aria-busy", "true");
    if (!keepNotice) this.errorTarget.hidden = true;
    controls.forEach((control) => {
      control.disabled = true;
    });

    let failed = false;
    try {
      const response = await fetch(submitter.formAction, {
        method: this.element.method,
        body: data,
        headers: { Accept: "text/html" },
        signal: this.request.signal,
      });
      if (!response.ok || response.redirected) throw new Error("Form preview failed");

      const page = new DOMParser().parseFromString(await response.text(), "text/html");
      const replacement = page.getElementById(this.element.id);
      if (!replacement) throw new Error("Form preview is missing the form");
      replacement.querySelectorAll('[data-protocol-editor-target~="fallback"]').forEach((button) => {
        button.hidden = true;
      });

      const openRows = reopen ? Array.from(this.element.querySelectorAll("details[open]"), (row) => row.id) : [];
      this.element.replaceWith(replacement);
      this.reopen(replacement, openRows);
      this.refocus(focus);
      restore(replacement);
    } catch (error) {
      failed = error.name !== "AbortError";
    } finally {
      controls.forEach((control) => {
        control.disabled = false;
      });
      this.element.removeAttribute("aria-busy");
      this.request = null;
    }
    if (failed) fail();
  }

  // The server opens only the row an action touched or one holding an error.
  reopen(replacement, ids) {
    if (replacement.querySelector("details[open]")) return;

    replacement.querySelectorAll("details").forEach((row) => {
      if (ids.includes(row.id)) row.open = true;
    });
  }

  // Disabling the form for the request takes focus off a field, so it is read
  // before then. A row's summary has no id of its own and is found by its row.
  focusedPlace() {
    const active = document.activeElement;
    if (!active || !this.element.contains(active)) return null;
    if (active.id) return { id: active.id };

    const row = active.tagName === "SUMMARY" ? active.closest("details") : null;
    return row?.id ? { row: row.id } : null;
  }

  refocus(place) {
    if (!place) return;

    const target = place.id
      ? document.getElementById(place.id)
      : document.getElementById(place.row)?.querySelector(":scope > summary");
    target?.focus({ preventScroll: true });
  }

  scroller(element) {
    const main = element.closest("main");
    return main && ["auto", "scroll"].includes(window.getComputedStyle(main).overflowY) ? main : window;
  }
}
